import { Component, OnInit, SecurityContext } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PledgeDao } from '@app/dao/pledge/pledge.dao';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, catchError, filter, map, switchMap } from 'rxjs';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';
import { DOCUMENT_EDITOR_TOOLBAR_BUTTONS } from '../document-verification/froala-editor/froala-editor.component';
import { RcdaRecommendationProposalDialogComponent } from './rcda-recommendation-proposal-dialog/rcda-recommendation-proposal-dialog.component';

const STYLE_BLOCKS = /<style[^>]*>[\s\S]*?<\/style>/gi;

@UntilDestroy()
@Component({
  selector: 'rcda-recommandation',
  templateUrl: './rcda-recommandation.component.html',
  styleUrls: ['./rcda-recommandation.component.scss'],
  standalone: false,
})
export class RcdaRecommandationComponent
  extends EaSubscriptionBuilder
  implements OnInit
{
  aiAvailable = false;
  toolbarButtons = DOCUMENT_EDITOR_TOOLBAR_BUTTONS;
  private aiAvailabilityPledgeId: number = null;

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    private pledgeDao: PledgeDao,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    this.subscriptionFacade.initForm(this);
  }

  initForm(entity: any, options?) {
    super.initForm(entity, options);
    this.loadAiAvailability(entity?.id);
  }

  /**
   * Demande une proposition à l'IA sous le chargement global du funnel, puis la soumet au
   * conseiller dans une modale. Les erreurs sont notifiées par le service de chargement.
   */
  generateWithAi() {
    this.subscriptionFacade.loadingService
      .showLoadingUntilCompleted(this.pledgeDao.proposeRcdaRecommendation(this.entity.id))
      .pipe(
        catchError(() => EMPTY),
        switchMap(proposal =>
          this.dialog
            .open(RcdaRecommendationProposalDialogComponent, {
              width: '800px',
              maxWidth: '92vw',
              data: { html: proposal.html },
            })
            .afterClosed()
            .pipe(
              filter(insert => insert === true),
              map(() => proposal.html),
            ),
        ),
        untilDestroyed(this),
      )
      .subscribe(html => this.insertAiProposal(html));
  }

  onSubmit() {
    this.form.markAsDirty();
    this.subscriptionFacade.submitPledge(this);
  }

  prev() {
    this.form.markAsDirty();
    this.subscriptionFacade.submitPledgeWithoutNextStep(this, 'pledge', () => {
      this.subscriptionFacade.previousStep();
    });
  }

  /**
   * Remplace le contenu de l'éditeur par la proposition nettoyée, en conservant les blocs
   * de style de l'organisation placés en tête de la recommandation.
   */
  private insertAiProposal(html: string) {
    const control = this.form.get('recommandationRcda');
    const organizationStyles = (control.value?.match(STYLE_BLOCKS) || []).join('');
    control.setValue(organizationStyles + this.sanitizer.sanitize(SecurityContext.HTML, html));
    this.form.markAsDirty();
  }

  private loadAiAvailability(pledgeId: number) {
    if (pledgeId == null || pledgeId === this.aiAvailabilityPledgeId) {
      return;
    }
    this.aiAvailabilityPledgeId = pledgeId;
    this.aiAvailable = false;
    this.pledgeDao
      .getRcdaRecommendationAiAvailability(pledgeId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: availability => (this.aiAvailable = availability.available),
        error: () => (this.aiAvailable = false),
      });
  }
}
